/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { FrontContent } from '../components/frontContent';
import FromResponse from '../components/fromResponse';
import ContactForm from '../components/contactForm';
import ChooseYourPath from '../components/chooseYourPath';
import { onClickGoogleAnalyticsEvent } from '../utils/ga-utils';
import Observe from '../utils/mutationUtils';
import classNames from 'classnames';
import { BiButton } from 'bi-web-components-react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { BiIconChat, BiIconCross } from '@bi-digital/icons-react';
import { ComponentSelector } from '../types';

type State = {
    chatDisabled: boolean;
    showChat: boolean;
    selector: ComponentSelector;
    innerChatButton: string;
    openedFromUrlParam: boolean;
};
type window = typeof window & {
    rowPageProperties: { disableChat: string };
};

declare let chatParams: any | null;

export const Page = () => {
    const { t, i18n } = useTranslation('chat');
    const isSmallScreen = useMediaQuery('(max-width: 770px)');

    const openFromUrlParam = new URLSearchParams(window.location.search).get('contactusopen');
    const openFromUrl = openFromUrlParam === 'true';

    const [state, setState] = useState<State>({
        showChat: openFromUrl,
        chatDisabled: ((window as window)?.rowPageProperties?.disableChat ?? 'False') === 'True',
        selector: 'front',
        innerChatButton: 'loading',
        openedFromUrlParam: openFromUrl,
    });

    useEffect(() => {
        Observe('message', found);
    }, []);

    function found(val: boolean) {
        setState((prev) => ({ ...prev, innerChatButton: val === true ? 'active' : 'disabled' }));
    }

    function bindInteractionStudio(startChatForm: HTMLDivElement) {
        const startBtn = startChatForm.getElementsByClassName(
            'startButton uiButton--default uiButton embeddedServiceSidebarButton',
        );
        const startChatBtn = startBtn && startBtn.length === 1 ? startBtn[0] : null;

        if (startChatBtn) {
            injectPrivacyStatement(i18n.language, t);

            if (chatParams) {
                (startChatBtn as HTMLButtonElement).click();
            }
        }
    }

    // Toggle body class when showChat changes
    useEffect(() => {
        document.body.classList.toggle('chat-shown', state.showChat);
        if (state.showChat) {
            setComponent('front');
        }
    }, [state.showChat]);

    function clickDaButt(category: string, action: string, label: string, startChatForm: any = null) {
        setState((prev) => ({ ...prev, showChat: !state.showChat }));
        onClickGoogleAnalyticsEvent(category, action, label);
        setComponent('front');

        if (startChatForm) {
            bindInteractionStudio(startChatForm);
        }
    }

    function clickTheChatBtn() {
        const btn2 = document.getElementById('innerChatButton') as any;
        if (btn2 === null || btn2.disabled || btn2.classList.contains('disabledChatBtn')) return;
        else {
            try {
                setComponent('chooseYourPath');
            } catch {
                //console.log(e);
            }
        }
        onClickGoogleAnalyticsEvent('Chat', 'Chat with us', window.location.href);
    }

    function setComponent(component: ComponentSelector) {
        setState((prev) => ({ ...prev, selector: component }));
    }

    function componentSelector(selector: string) {
        if (selector === 'front')
            return (
                <FrontContent
                    innerChatButton={state.innerChatButton}
                    click={clickTheChatBtn}
                    setComponent={setComponent}
                />
            );
        else if (selector === 'contactForm') return <ContactForm setComponent={setComponent} />;
        else if (selector === 'fromResponse') return <FromResponse setComponent={setComponent} />;
        else if (selector === 'chooseYourPath')
            return <ChooseYourPath setComponent={setComponent} clickDaButt={clickDaButt} />;
    }

    const contactSnapClasses = state.showChat ? 'contactSnap show' : 'contactSnap dontShow';
    const chatTitle = state.selector === 'contactForm' ? t('headerTitle') : t('talkWithUs');
    if (state.chatDisabled) return null;
    return (
        <div className='chat-section'>
            {!state.showChat && (
                <div id='chatButtonIcon'>
                    <div className='chatHelpButton'>
                        <BiButton
                            icon={isSmallScreen}
                            aria-label={t('talkWithUs')}
                            onClick={() => clickDaButt('Chat', 'Open', window.location.href)}
                        >
                            {isSmallScreen ? (
                                <BiIconChat size='medium' title={t('talkWithUs')} />
                            ) : (
                                <>
                                    <BiIconChat slot='icon-left' aria-hidden='true' />
                                    {t('talkWithUs')}
                                </>
                            )}
                        </BiButton>
                    </div>
                </div>
            )}

            <div id='contactSnap' className={contactSnapClasses}>
                <div
                    className={classNames('snapinHeader', {
                        hide: state.openedFromUrlParam,
                    })}
                >
                    <div className='nowrappy'>
                        <span className='chatTitle'>{chatTitle}</span>
                        <BiButton
                            id='contactSnapCloseBtn'
                            className='x-styling'
                            onClick={() => setState((prev) => ({ ...prev, showChat: false }))}
                            aria-label={t('closeContactSnapIn')}
                            variant='text'
                            icon
                            size='small'
                            dark
                        >
                            <BiIconCross title={t('closeContactSnapIn')} />
                        </BiButton>
                    </div>
                </div>
                <div id='contentBody'>{componentSelector(state.selector)}</div>
            </div>
        </div>
    );
};

// Lets pray that the new chat will be better
function injectPrivacyStatement(language: string, t: TFunction<'chat'>) {
    // Div in saleforceChatForm
    const fieldListDiv = document.querySelector('.fieldList');
    // Create a new <p> element
    const newParagraph = document.createElement('div');
    // Add some text to the new <p> element (optional)
    newParagraph.innerHTML = `
        <p style="font-size: 14px; text-align: center; margin-top: 8px; font-style: italic;">
            ${t('gdpr')}
            <bi-link
                color="blue"
                href="${language === 'no' ? '/personvern' : '/privacypolicy'}"
                size="small"
                state="noline"
                target="_blank"
            >
            ${t('gdprPrivacy')}
            </bi-link>
        </p>`;
    // Append the new <p> element as the next child of the div
    fieldListDiv?.appendChild(newParagraph);

    const elements = document.querySelectorAll<HTMLElement>('.inputSelect.embeddedServiceSidebarFormField');
    // Check if there are more than one dropdown in the injected form
    if (elements.length > 1) {
        // Hide the last dropdown
        elements[elements.length - 1].style.display = 'none';
    }
}
